import React, { useState, useEffect } from 'react'
import { Button, Box, Paper, makeStyles } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { KeyboardArrowRight } from '@mui/icons-material'
import SpeakerListing from 'views/admin/people/components/SpeakerListing'
import AttendeeListing from 'views/admin/people/components/AttendeeListing'
import InviteSpeakerModal from './InviteSpeakerModal'
import ImportSpeakersModal from './ImportSpeakersModal'
import ImportAttendeesModal from './ImportAttendeesModal'
import { AttendeeRoles, orgRepresentativeStatus } from 'constants/index'
import moment from 'moment'
import { toast } from 'utils/notify'
import { bulkImportAttendees } from 'store/attendee'
import { useDispatch, useSelector } from 'react-redux'
import CustomTabs from 'components/CustomTabs'

const peopleTabs = ['Speakers', 'Attendees']

const People = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importAttendeesModalOpen, setImportAttendeesModalOpen] = useState(false)
  const [roleFilter, setRoleFilter] = useState('all')
  const { user } = useSelector((state) => state.auth)
  const { speakers } = useSelector((state) => state.speaker)
  const { attendees } = useSelector((state) => state.attendee)

  // Get the search params from the URL
  const searchParams = new URLSearchParams(location.search)
  // Get tab from URL or default to 'speakers'
  const tab = searchParams.get('tab') || 'speakers'

  // Ensure the tab param exists in URL when component mounts
  useEffect(() => {
    if (!searchParams.has('tab')) {
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.set('tab', 'speakers')
      history.replace({
        pathname: location.pathname,
        search: newSearchParams.toString()
      })
    }
  }, [location.search, history, location.pathname, searchParams])

  // Update the URL when tab changes
  const handleTabChange = (newValue) => {
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', newValue.toLowerCase())
    history.replace({
      pathname: location.pathname,
      search: newSearchParams.toString()
    })
    setRoleFilter('all') // Reset filter when changing tabs
  }

  const handleRedirect = (route) => {
    history.push(`${location.pathname}/speakers/${route}`)
  }

  const openInviteModal = () => {
    setInviteModalOpen(true)
  }

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false)
  }

  const openImportModal = () => {
    setImportModalOpen(true)
  }

  const handleCloseImportModal = () => {
    setImportModalOpen(false)
  }

  const openImportAttendeesModal = () => setImportAttendeesModalOpen(true)
  const closeImportAttendeesModal = () => setImportAttendeesModalOpen(false)

  const handleImportAttendees = async (attendees) => {
    try {
      const attendeesData = attendees?.map((att) => ({
        firstName: att?.firstName || '',
        lastName: att?.lastName || '',
        email: att?.email || '',
        role: att?.role || AttendeeRoles.ATTENDEE,
        credential: att?.credential || '',
        position: att?.position || '',
        company: att?.organization || '',

        phoneNumber: att?.phone || '',
        city: att?.city || '',
        state: att?.state || '',
        country: att?.country || '',

        status: orgRepresentativeStatus.NOT_NOTIFIED,
        conferenceId: user?.conferenceId,
        createdAt: moment().utc().valueOf()
      }))
      const results = await dispatch(bulkImportAttendees(attendeesData, user?.conferenceId))
      return results
    } catch (error) {
      console.error('Error importing attendees:', error)
      toast.error('Could not import attendees. Please try again.')
      return false
    }
  }

  return (
    <Box>
      <Box className={classes.tabRow}>
        <CustomTabs
          tabs={peopleTabs}
          activeTab={tab === 'attendees' ? 'Attendees' : 'Speakers'}
          valueKey="label"
          counts={{
            Speakers: speakers?.length || 0,
            Attendees: attendees?.length || 0
          }}
          onChange={handleTabChange}
        />

        <Box className={classes.buttonGroupBox}>
          {tab === 'speakers' && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<KeyboardArrowRight />}
                onClick={() => handleRedirect('new')}>
                Create
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={openInviteModal}
                endIcon={<KeyboardArrowRight />}>
                Invite
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              tab === 'speakers' ? openImportModal() : openImportAttendeesModal()
            }}
            endIcon={<KeyboardArrowRight />}>
            Import via CSV
          </Button>
        </Box>
      </Box>

      <Box className={classes.contentWrapper}>
        <Paper variant="outlined" style={{ marginTop: '16px' }}>
          {tab === 'speakers' && <SpeakerListing />}
          {tab === 'attendees' && <AttendeeListing roleFilter={roleFilter} />}
        </Paper>
      </Box>

      <InviteSpeakerModal open={inviteModalOpen} onClose={handleCloseInviteModal} />
      <ImportSpeakersModal open={importModalOpen} onClose={handleCloseImportModal} />
      <ImportAttendeesModal
        open={importAttendeesModalOpen}
        onClose={closeImportAttendeesModal}
        onImport={handleImportAttendees}
      />
    </Box>
  )
}

export default People

const useStyles = makeStyles((theme) => ({
  tabRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actionBar: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontWeight: 500,
    fontSize: '18px',
    color: '#2c3e50'
  },
  buttonGroupBox: {
    margin: '24px 0 16px 0',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  contentWrapper: {
    marginTop: theme.spacing(2)
  }
}))
